import { cloneElement, useCallback, useMemo, useState, type JSX, type ReactElement } from 'react';
import clsx from 'clsx';
import { toast } from 'sonner';
import type { ZodIssue } from 'zod';
import { Form, FormActions, FormField } from '@/components/form';
import { PillButton } from '@/components/entity/PillButton';
import { useDashboardUser } from '@/routes/dashboard/DashboardRouter';
import type {
  FloorLevelCode,
  UnitAvailabilityStatus,
  UnitFormValues,
  UnitPossessionTimeline,
  UnitSummary,
} from '@/lib/types/property';
import { PUBLIC_READINESS_LABELS } from '@/components/public/properties';
import {
  useUnitCreateMutation,
  useUnitDeleteMutation,
  useUnitUpdateMutation,
} from '@/hooks/useDashboardProperties';
import { unitFormSchema } from '@/lib/validation/property';
import { trackUnitDelete } from '@/lib/analytics/events';
import {
  PROPERTY_MEDIA_ALLOWED_TYPES,
  PROPERTY_MEDIA_MAX_BYTES,
  PROPERTY_MEDIA_MAX_FILES,
} from '@/lib/constants/propertyMedia';
import {
  isAllowedPropertyMediaType,
  isWithinPropertyMediaSizeLimit,
  parsePropertyMediaUrl,
  removePropertyMediaFiles,
  uploadPropertyMediaFile,
} from '@/lib/storage/propertyMedia';
import { Drawer } from '@/components/layout/Drawer';
import { createInternalUnitCode } from '@/lib/utils/unitIdentifiers';
import { formatFloorLevelLabel, formatUnitLabel } from '@/lib/utils/propertyFormatting';
import { calculateMonthlyRent } from '@/lib/utils/rentCalculations';
import { roundCurrency } from '@/lib/utils/currency';
import {
  formatRentInputValue,
  sanitizeRentInput,
  formatRentNumberToInput,
} from '@/routes/dashboard/unitForm/rentInput';

interface UnitEditDrawerProps {
  propertyId: string;
  unit: UnitSummary;
  layout: UnitFormLayout;
  trigger: JSX.Element;
}

interface UnitCreateDrawerProps {
  propertyId: string;
  layout: UnitFormLayout;
  trigger: JSX.Element;
}

type UnitFormState = {
  unitCode: string;
  unitLabel: string;
  floorLevel: FloorLevelCode | '';
  areaSf: string;
  minDivisibleSf: string;
  availabilityStatus: UnitAvailabilityStatus;
  possessionTimeline: UnitPossessionTimeline;
  netRentPerSf: string;
  additionalRentPerSf: string;
  incentives: string;
  potentiallyDivisible: boolean;
  contactEmail: string;
  displayOnPublicWebsite: boolean;
  published: boolean;
  heroImageUrl: string;
  galleryImageUrls: string[];
};

type FormErrors = Partial<Record<keyof UnitFormState, string>>;

type UnitFormMode = 'edit' | 'create';
type UnitFormLayout = 'desktop' | 'tablet' | 'mobile';

const UNIT_STATUS_OPTIONS: UnitAvailabilityStatus[] = ['available', 'unavailable', 'leased'];

const UNIT_POSSESSION_TIMELINE_OPTIONS: UnitPossessionTimeline[] = ['immediate', 'negotiable'];

const NUMBERED_FLOORS: FloorLevelCode[] = Array.from(
  { length: 30 },
  (_value, index) => String(index + 1) as FloorLevelCode,
);

const FLOOR_LEVEL_OPTIONS: FloorLevelCode[] = (
  ['B3', 'B2', 'B1', 'G', 'M'] as FloorLevelCode[]
).concat(NUMBERED_FLOORS);

interface UnitFormDrawerProps {
  propertyId: string;
  unit?: UnitSummary;
  layout: UnitFormLayout;
  trigger: JSX.Element;
  mode: UnitFormMode;
}

export function UnitEditDrawer({
  propertyId,
  unit,
  layout,
  trigger,
}: UnitEditDrawerProps): JSX.Element {
  return (
    <UnitFormDrawer
      propertyId={propertyId}
      unit={unit}
      layout={layout}
      trigger={trigger}
      mode="edit"
    />
  );
}

export function UnitCreateDrawer({
  propertyId,
  layout,
  trigger,
}: UnitCreateDrawerProps): JSX.Element {
  return <UnitFormDrawer propertyId={propertyId} layout={layout} trigger={trigger} mode="create" />;
}

function UnitFormDrawer({
  propertyId,
  unit,
  layout,
  trigger,
  mode,
}: UnitFormDrawerProps): JSX.Element {
  const user = useDashboardUser();
  const updateMutation = useUnitUpdateMutation({ user, propertyId });
  const createMutation = useUnitCreateMutation({ user, propertyId });
  const deleteMutation = useUnitDeleteMutation({ user, propertyId });
  const [open, setOpen] = useState(false);
  const [formState, setFormState] = useState<UnitFormState>(() =>
    mode === 'edit' && unit ? createInitialState(unit) : createEmptyState(),
  );
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [heroUploadError, setHeroUploadError] = useState<string | null>(null);
  const [isUploadingHero, setIsUploadingHero] = useState(false);
  const [galleryUploadError, setGalleryUploadError] = useState<string | null>(null);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);
  const monthlyRent = useMemo(() => {
    return calculateMonthlyRent({
      netRentPerSf: toNumber(formState.netRentPerSf),
      additionalRentPerSf: toNumber(formState.additionalRentPerSf),
      unitSqFt: toNumber(formState.areaSf),
      rentFrequency: 'annually',
    });
  }, [formState.additionalRentPerSf, formState.areaSf, formState.netRentPerSf]);
  const monthlyRentLabel = useMemo(() => {
    if (monthlyRent === null) {
      return '—';
    }
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 2,
    }).format(monthlyRent);
  }, [monthlyRent]);
  const isEdit = mode === 'edit';
  const maxImageSizeGb = useMemo(
    () => Math.round(PROPERTY_MEDIA_MAX_BYTES / (1024 * 1024 * 1024)),
    [],
  );
  const heroAcceptTypes = useMemo(() => PROPERTY_MEDIA_ALLOWED_TYPES.join(','), []);
  const isUploadingMedia = isUploadingHero || isUploadingGallery;
  const galleryUrls = useMemo(
    () => formState.galleryImageUrls.map((url) => url.trim()).filter((url) => url.length > 0),
    [formState.galleryImageUrls],
  );
  const isGalleryFull = galleryUrls.length >= PROPERTY_MEDIA_MAX_FILES;

  const formId = isEdit && unit ? `unit-form-${unit.id}` : 'unit-create-form';

  const resetFormState = useCallback(() => {
    if (isEdit && unit) {
      setFormState(createInitialState(unit));
    } else {
      setFormState(createEmptyState());
    }
    setErrors({});
    setFormError(null);
    setIsSubmitting(false);
    setHeroUploadError(null);
    setIsUploadingHero(false);
    setGalleryUploadError(null);
    setIsUploadingGallery(false);
  }, [isEdit, unit]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        resetFormState();
        return;
      }
      setFormError(null);
    },
    [resetFormState],
  );

  const triggerWithHandler = useMemo<ReactElement>(() => {
    const element = trigger as ReactElement<{ onClick?: (event: React.MouseEvent) => void }>;
    return cloneElement(element, {
      onClick: (event: React.MouseEvent) => {
        if (typeof element.props.onClick === 'function') {
          element.props.onClick(event);
        }
        handleOpenChange(true);
      },
    });
  }, [handleOpenChange, trigger]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    if (!event.currentTarget.reportValidity()) {
      return;
    }
    event.preventDefault();
    setFormError(null);
    if (isUploadingMedia) {
      setFormError('Wait for the media upload to finish.');
      return;
    }
    const correlationId = createCorrelationId();
    console.info(
      JSON.stringify({
        ts: new Date().toISOString(),
        level: 'info',
        event: 'unit_form_submit_start',
        correlationId,
        propertyId,
        unitId: unit?.id ?? null,
        mode,
      }),
    );
    const payload = buildPayload({ propertyId, state: formState, unit });
    const validation = unitFormSchema.safeParse(payload);

    if (!validation.success) {
      const mapped = mapIssues(validation.error.issues);
      setErrors(mapped);
      setFormError('Fix the highlighted fields to continue.');
      console.warn(
        JSON.stringify({
          ts: new Date().toISOString(),
          level: 'warn',
          event: 'unit_form_validation_failed',
          correlationId,
          issues: validation.error.issues.map((issue) => ({
            path: issue.path,
            message: issue.message,
          })),
        }),
      );
      return;
    }

    const displayLabel = formatUnitLabel(validation.data);
    setErrors({});
    setIsSubmitting(true);

    if (isEdit) {
      if (!unit) {
        setFormError('Unit is missing. Reload and try again.');
        setIsSubmitting(false);
        return;
      }

      updateMutation.mutate(
        { unit, values: validation.data },
        {
          onSuccess: () => {
            toast.success(`${displayLabel} updated.`);
            console.info(
              JSON.stringify({
                ts: new Date().toISOString(),
                level: 'info',
                event: 'unit_form_submit_success',
                correlationId,
                unitId: unit.id,
                propertyId,
              }),
            );
            setOpen(false);
          },
          onError: (error) => {
            setFormError(error instanceof Error ? error.message : 'Unable to save unit.');
            console.error(
              JSON.stringify({
                ts: new Date().toISOString(),
                level: 'error',
                event: 'unit_form_submit_error',
                correlationId,
                unitId: unit.id,
                propertyId,
                message: error instanceof Error ? error.message : 'Unknown error',
              }),
            );
          },
          onSettled: () => {
            setIsSubmitting(false);
          },
        },
      );
    } else {
      createMutation.mutate(
        { values: validation.data },
        {
          onSuccess: (created) => {
            toast.success(`${displayLabel || formatUnitLabel(created)} created.`);
            console.info(
              JSON.stringify({
                ts: new Date().toISOString(),
                level: 'info',
                event: 'unit_form_submit_success',
                correlationId,
                unitId: created.id ?? null,
                propertyId,
              }),
            );
            setOpen(false);
          },
          onError: (error) => {
            setFormError(error instanceof Error ? error.message : 'Unable to create unit.');
            console.error(
              JSON.stringify({
                ts: new Date().toISOString(),
                level: 'error',
                event: 'unit_form_submit_error',
                correlationId,
                unitId: null,
                propertyId,
                message: error instanceof Error ? error.message : 'Unknown error',
              }),
            );
          },
          onSettled: () => {
            setIsSubmitting(false);
          },
        },
      );
    }
  };

  const handleDelete = (): void => {
    if (!unit || isSubmitting || isUploadingMedia) {
      return;
    }

    const confirmed = window.confirm(
      `Delete ${formatUnitLabel(unit)}? This removes the unit from this property. This cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    setIsSubmitting(true);

    deleteMutation.mutate(
      { unit },
      {
        onSuccess: () => {
          toast.success(`${formatUnitLabel(unit)} deleted.`);
          trackUnitDelete({ unitId: unit.id, propertyId });
          setOpen(false);
        },
        onError: (error) => {
          toast.error(`Unable to delete ${formatUnitLabel(unit)}.`, {
            description: error instanceof Error ? error.message : undefined,
          });
        },
        onSettled: () => {
          setIsSubmitting(false);
        },
      },
    );
  };

  const handleHeroFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const files = Array.from(event.target.files ?? []).slice(0, 1);
    if (files.length === 0) {
      return;
    }

    setHeroUploadError(null);

    const [file] = files;
    if (!isAllowedPropertyMediaType(file)) {
      setHeroUploadError(`Unsupported file type: ${file.type || file.name}.`);
      event.target.value = '';
      return;
    }

    if (!isWithinPropertyMediaSizeLimit(file)) {
      setHeroUploadError(`Each image must be under ${maxImageSizeGb.toString()}GB.`);
      event.target.value = '';
      return;
    }

    setIsUploadingHero(true);

    try {
      if (formState.heroImageUrl) {
        const parsed = parsePropertyMediaUrl(formState.heroImageUrl);
        if (parsed) {
          await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
        }
      }

      const result = await uploadPropertyMediaFile({ propertyId, file });
      if (result.url) {
        setFormState((prev) => ({ ...prev, heroImageUrl: result.url }));
        toast.success('Uploaded hero image.');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed. Please try again.';
      setHeroUploadError(message);
      toast.error(message);
    } finally {
      setIsUploadingHero(false);
      event.target.value = '';
    }
  };

  const handleRemoveHeroImage = async (): Promise<void> => {
    if (!formState.heroImageUrl) {
      return;
    }
    setHeroUploadError(null);
    setIsUploadingHero(true);
    try {
      const parsed = parsePropertyMediaUrl(formState.heroImageUrl);
      if (parsed) {
        await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
      }
      setFormState((prev) => ({ ...prev, heroImageUrl: '' }));
      toast.success('Removed hero image.');
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to remove image from storage.';
      setHeroUploadError(message);
      toast.error(message);
    } finally {
      setIsUploadingHero(false);
    }
  };

  const handleGalleryFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const selectedFiles = Array.from(event.target.files ?? []);
    if (selectedFiles.length === 0) {
      return;
    }

    setGalleryUploadError(null);

    const remainingSlots = PROPERTY_MEDIA_MAX_FILES - galleryUrls.length;
    if (remainingSlots <= 0) {
      setGalleryUploadError(`You can upload up to ${PROPERTY_MEDIA_MAX_FILES.toString()} images.`);
      event.target.value = '';
      return;
    }

    const files = selectedFiles.slice(0, remainingSlots);
    if (selectedFiles.length > remainingSlots) {
      toast.info(
        `Uploading ${remainingSlots.toString()} of ${selectedFiles.length.toString()} selected images.`,
      );
    }

    const invalidType = files.find((file) => !isAllowedPropertyMediaType(file));
    if (invalidType) {
      setGalleryUploadError(`Unsupported file type: ${invalidType.type || invalidType.name}.`);
      event.target.value = '';
      return;
    }

    const tooLarge = files.find((file) => !isWithinPropertyMediaSizeLimit(file));
    if (tooLarge) {
      setGalleryUploadError(`Each image must be under ${maxImageSizeGb.toString()}GB.`);
      event.target.value = '';
      return;
    }

    setIsUploadingGallery(true);
    let uploadedCount = 0;

    try {
      for (const file of files) {
        const result = await uploadPropertyMediaFile({ propertyId, file });
        if (typeof result.url === 'string' && result.url.length > 0) {
          uploadedCount += 1;
          setFormState((prev) => ({
            ...prev,
            galleryImageUrls: [...prev.galleryImageUrls, result.url],
          }));
        }
      }

      if (uploadedCount === 1) {
        toast.success('Uploaded photo.');
      } else if (uploadedCount > 1) {
        toast.success(`Uploaded ${uploadedCount.toString()} photos.`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed. Please try again.';
      setGalleryUploadError(message);
      toast.error(message);
    } finally {
      setIsUploadingGallery(false);
      event.target.value = '';
    }
  };

  const handleRemoveGalleryImage = async (url: string): Promise<void> => {
    setGalleryUploadError(null);
    setIsUploadingGallery(true);
    let removed = false;
    try {
      const parsed = parsePropertyMediaUrl(url);
      if (parsed) {
        await removePropertyMediaFiles([`${parsed.bucket}/${parsed.objectPath}`]);
      }
      removed = true;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to remove image from storage.';
      setGalleryUploadError(message);
      toast.error(message);
    } finally {
      if (removed) {
        setFormState((prev) => ({
          ...prev,
          galleryImageUrls: prev.galleryImageUrls.filter((existing) => existing !== url),
        }));
        toast.success('Removed photo.');
      }
      setIsUploadingGallery(false);
    }
  };

  const inputClass =
    'rounded-2xl border border-surface-subtle px-4 py-2.5 text-base text-text-primary shadow-sm transition focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm';

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      title={isEdit && unit ? `Edit ${formatUnitLabel(unit)}` : 'Add unit'}
      description={
        isEdit && unit
          ? `${PUBLIC_READINESS_LABELS[unit.availability.status]} • ${formatFloorLevelLabel(unit.floorLevel)}`
          : 'Create a new unit for this property.'
      }
      trigger={triggerWithHandler}
      size={layout === 'desktop' ? 'md' : 'sm'}
      footerContent={
        <FormActions justify="between">
          <div className="flex flex-wrap gap-3">
            {isEdit ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isSubmitting || isUploadingMedia}
                className="rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-60"
              >
                Delete unit
              </button>
            ) : null}
            <button
              type="button"
              className="rounded-full px-4 py-2 text-sm font-semibold text-text-primary/80 transition hover:bg-surface-subtle"
              onClick={() => {
                resetFormState();
                setOpen(false);
              }}
            >
              Cancel
            </button>
          </div>
          <PillButton type="submit" form={formId} disabled={isSubmitting || isUploadingMedia}>
            {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create unit'}
          </PillButton>
        </FormActions>
      }
    >
      <Form id={formId} gap="lg" onSubmit={handleSubmit}>
        <section className="grid gap-4 md:grid-cols-2">
          <FormField
            id="unit-label"
            label="Unit label"
            description="Shown on public unit cards and detail pages. Example: 302B."
            error={errors.unitLabel}
            required
          >
            <input
              type="text"
              value={formState.unitLabel}
              onChange={(event) => {
                setFormState((prev) => ({ ...prev, unitLabel: event.target.value }));
              }}
              maxLength={64}
              className={inputClass}
            />
          </FormField>
          <FormField
            id="unit-floor"
            label="Floor"
            description="Optional. Shown as the floor on public unit details when set."
            error={errors.floorLevel}
          >
            <select
              value={formState.floorLevel}
              onChange={(event) => {
                setFormState((prev) => ({
                  ...prev,
                  floorLevel: event.target.value as FloorLevelCode,
                }));
              }}
              className={clsx(inputClass, 'bg-white')}
            >
              <option value="">No floor selected</option>
              {FLOOR_LEVEL_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </FormField>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <FormField
            id="unit-area"
            label="Area (sf)"
            description="Displayed as the unit size on public listings and detail pages. Example: 3500."
            error={errors.areaSf}
            required
          >
            <input
              type="number"
              inputMode="decimal"
              min={1}
              value={formState.areaSf}
              onChange={(event) => {
                setFormState((prev) => ({ ...prev, areaSf: event.target.value }));
              }}
              className={inputClass}
            />
          </FormField>
          <FormField
            id="unit-divisible"
            label="Minimum divisible sf"
            description="If set, public listings show a size range or a Min divisible note. Example: 1200."
            error={errors.minDivisibleSf}
          >
            <input
              type="number"
              inputMode="decimal"
              min={0}
              value={formState.minDivisibleSf}
              onChange={(event) => {
                setFormState((prev) => ({ ...prev, minDivisibleSf: event.target.value }));
              }}
              className={inputClass}
            />
          </FormField>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <FormField
            id="unit-availability"
            label="Move-in readiness"
            description="Controls whether the unit is ready for immediate occupancy or flexible move-in timing."
            error={errors.possessionTimeline}
            hint="This only controls move-in timing copy. It does not control public website visibility or realtor outreach emails."
            required
          >
            <select
              value={formState.possessionTimeline}
              onChange={(event) => {
                setFormState((prev) => ({
                  ...prev,
                  possessionTimeline: event.target.value as UnitPossessionTimeline,
                }));
              }}
              className={clsx(inputClass, 'bg-white')}
            >
              {UNIT_POSSESSION_TIMELINE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option === 'negotiable' ? 'Negotiable' : 'Immediate'}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            id="unit-readiness"
            label="Unit availability status"
            description="Tracks the unit's current leasing status for internal and public detail views."
            error={errors.availabilityStatus}
            hint="This status does not control whether the unit appears on the public website."
            required
          >
            <select
              value={formState.availabilityStatus}
              onChange={(event) => {
                setFormState((prev) => ({
                  ...prev,
                  availabilityStatus: event.target.value as UnitAvailabilityStatus,
                }));
              }}
              className={clsx(inputClass, 'bg-white')}
            >
              {UNIT_STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {PUBLIC_READINESS_LABELS[option]}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            id="unit-display-public"
            label="Display on public website?"
            description="Choose whether this unit appears on the public website."
            error={errors.displayOnPublicWebsite}
            hint="Yes shows this unit publicly regardless of move-in timing, move-in date, or availability status."
            required
          >
            <select
              value={formState.displayOnPublicWebsite ? 'yes' : 'no'}
              onChange={(event) => {
                setFormState((prev) => ({
                  ...prev,
                  displayOnPublicWebsite: event.target.value === 'yes',
                }));
              }}
              className={clsx(inputClass, 'bg-white')}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </FormField>
        </section>

        <section className="grid gap-4">
          <FormField
            id="unit-contact"
            label="Contact email"
            description="Used as the public contact email for inquiries and showing requests. Example: leasing@strashindevelopments.com."
            error={errors.contactEmail}
            required
          >
            <input
              type="email"
              autoComplete="email"
              value={formState.contactEmail}
              onChange={(event) => {
                setFormState((prev) => ({ ...prev, contactEmail: event.target.value }));
              }}
              className={inputClass}
            />
          </FormField>
        </section>

        <FormField
          id="unit-hero-image"
          label="Hero image"
          description={`Optional. Shown on public unit cards and the unit detail hero. Accepted types: JPG, PNG, WebP, HEIC. Max ${maxImageSizeGb.toString()}GB.`}
          error={heroUploadError ?? errors.heroImageUrl}
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex h-28 w-40 items-center justify-center overflow-hidden rounded-xl border border-surface-subtle bg-surface-subtle/60">
                {formState.heroImageUrl ? (
                  <img
                    src={formState.heroImageUrl}
                    alt="Unit hero"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="h-full w-full" aria-hidden="true" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center justify-center rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-brand-primary transition focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-primary/40 hover:bg-brand-primary/5">
                  <input
                    type="file"
                    accept={heroAcceptTypes}
                    className="sr-only"
                    onChange={handleHeroFileChange}
                    disabled={isUploadingMedia}
                  />
                  {isUploadingHero
                    ? 'Uploading…'
                    : formState.heroImageUrl
                      ? 'Replace image'
                      : 'Upload image'}
                </label>
                {formState.heroImageUrl ? (
                  <button
                    type="button"
                    onClick={handleRemoveHeroImage}
                    disabled={isUploadingMedia}
                    className="text-sm font-semibold text-red-700 underline-offset-4 transition hover:underline disabled:opacity-60"
                  >
                    Remove image
                  </button>
                ) : null}
                <p className="text-xs text-text-primary/70">
                  Images are stored with the property and reused across layouts.
                </p>
              </div>
            </div>
            {heroUploadError !== null ? (
              <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">
                {heroUploadError}
              </p>
            ) : null}
          </div>
        </FormField>

        <FormField
          id="unit-gallery-images"
          label="Gallery images"
          description={`Optional. Shown in the unit photo gallery. Upload up to ${PROPERTY_MEDIA_MAX_FILES.toString()} images. Max ${maxImageSizeGb.toString()}GB each.`}
          error={galleryUploadError ?? errors.galleryImageUrls}
        >
          <div className="flex flex-col gap-4">
            <label
              className={clsx(
                'inline-flex w-fit items-center justify-center rounded-full border border-surface-subtle px-4 py-2 text-sm font-semibold text-brand-primary transition focus-within:outline-none focus-within:ring-2 focus-within:ring-brand-primary/40 hover:bg-brand-primary/5',
                (isUploadingMedia || isGalleryFull) && 'opacity-60',
              )}
            >
              <input
                type="file"
                accept={heroAcceptTypes}
                className="sr-only"
                multiple
                disabled={isUploadingMedia || isGalleryFull}
                onChange={handleGalleryFileChange}
              />
              {isUploadingGallery ? 'Uploading…' : isGalleryFull ? 'Gallery full' : 'Upload photos'}
            </label>
            {galleryUrls.length > 0 ? (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {galleryUrls.map((url) => (
                  <div
                    key={url}
                    className="flex flex-col overflow-hidden rounded-2xl border border-surface-subtle bg-white"
                  >
                    <img src={url} alt="Unit gallery" className="h-40 w-full object-cover" />
                    <button
                      type="button"
                      className="border-t border-surface-subtle px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:opacity-60"
                      disabled={isUploadingMedia}
                      onClick={() => {
                        void handleRemoveGalleryImage(url);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-primary/70">
                No unit photos yet. Upload images to populate the unit gallery.
              </p>
            )}
          </div>
        </FormField>

        <FormField
          id="unit-publish"
          label="Realtor outreach emails"
          description="Enable outreach emails to subscribed realtors for this unit."
          hint="When enabled, every saved unit change queues an outreach email. This setting does not control public website visibility."
        >
          <label className="flex items-center gap-3 text-sm font-medium text-text-primary">
            <input
              type="checkbox"
              checked={formState.published}
              onChange={(event) => {
                setFormState((prev) => ({ ...prev, published: event.target.checked }));
              }}
              className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary disabled:cursor-not-allowed"
            />
            {formState.published ? 'Outreach emails enabled' : 'Outreach emails disabled'}
          </label>
        </FormField>

        <section className="grid gap-4 md:grid-cols-2" data-ui="Suites.Edit.RentSection">
          <FormField
            id="unit-monthly-rent"
            label="Monthly rent (derived)"
            description="Calculated from annual NET $/SF, annual Additional Rent $/SF, and Unit Sq Ft."
            className="md:col-span-2"
          >
            <input
              type="text"
              value={monthlyRentLabel}
              readOnly
              aria-readonly="true"
              className={clsx(inputClass, 'bg-surface-muted/60 text-text-primary/70')}
            />
          </FormField>
        </section>

        <section className="grid gap-4 md:grid-cols-2">
          <FormField
            id="unit-net-rent"
            label="Annual net rent / SF"
            description="Shown as the annual base $/SF rate on public listings. Accepts up to two decimals, e.g., 24.50."
            error={errors.netRentPerSf}
            required
          >
            <input
              type="text"
              inputMode="decimal"
              value={formState.netRentPerSf}
              onChange={(event) => {
                const value = sanitizeRentInput(event.target.value);
                setFormState((prev) => ({ ...prev, netRentPerSf: value }));
              }}
              onBlur={() => {
                setFormState((prev) => ({
                  ...prev,
                  netRentPerSf: formatRentInputValue(prev.netRentPerSf),
                }));
              }}
              className={inputClass}
            />
          </FormField>
          <FormField
            id="unit-additional-rent"
            label="Annual additional rent / SF"
            description="Shown as the annual add-on $/SF rate in public rent details when applicable. Accepts up to two decimals, e.g., 8.00."
            error={errors.additionalRentPerSf}
          >
            <input
              type="text"
              inputMode="decimal"
              value={formState.additionalRentPerSf}
              onChange={(event) => {
                const value = sanitizeRentInput(event.target.value);
                setFormState((prev) => ({
                  ...prev,
                  additionalRentPerSf: value,
                }));
              }}
              onBlur={() => {
                setFormState((prev) => ({
                  ...prev,
                  additionalRentPerSf: formatRentInputValue(prev.additionalRentPerSf),
                }));
              }}
              className={inputClass}
            />
          </FormField>
        </section>

        <section className="flex flex-col gap-2">
          <FormField
            id="unit-divisible-toggle"
            label="Divisible"
            description="Shows the Divisible label on public listings."
            error={errors.potentiallyDivisible}
          >
            <label className="flex items-center gap-3 text-sm font-medium text-text-primary">
              <input
                type="checkbox"
                checked={formState.potentiallyDivisible}
                onChange={(event) => {
                  setFormState((prev) => ({
                    ...prev,
                    potentiallyDivisible: event.target.checked,
                  }));
                }}
                className="h-4 w-4 rounded border-surface-subtle text-brand-primary focus:ring-brand-primary"
              />
              {formState.potentiallyDivisible ? 'Yes' : 'No'}
            </label>
          </FormField>
          <FormField
            id="unit-incentives"
            label="Incentives"
            error={errors.incentives}
            description="If filled, public listings show an Incentives label and filter. Example: One month free."
          >
            <textarea
              rows={3}
              value={formState.incentives}
              onChange={(event) => {
                setFormState((prev) => ({ ...prev, incentives: event.target.value }));
              }}
              className={clsx(inputClass, 'min-h-[96px]')}
            />
          </FormField>
        </section>

        {formError !== null ? (
          <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {formError}
          </p>
        ) : null}
      </Form>
    </Drawer>
  );
}

function createInitialState(unit: UnitSummary): UnitFormState {
  return {
    unitCode: unit.unitCode,
    unitLabel: unit.unitLabel,
    floorLevel: unit.floorLevel ?? '',
    areaSf: toInput(unit.areaSf),
    minDivisibleSf: unit.minDivisibleSf != null ? toInput(unit.minDivisibleSf) : '',
    availabilityStatus: unit.availability.status,
    possessionTimeline: unit.possessionTimeline,
    netRentPerSf: formatRentNumberToInput(unit.rent.netRentPerSf),
    additionalRentPerSf: formatRentNumberToInput(unit.rent.additionalRentPerSf),
    incentives: unit.incentives ?? '',
    potentiallyDivisible: unit.potentiallyDivisible,
    contactEmail: unit.contactEmail,
    displayOnPublicWebsite: unit.displayOnPublicWebsite,
    heroImageUrl: unit.heroImageUrl ?? '',
    galleryImageUrls: unit.galleryImageUrls,
    published: unit.published,
  } satisfies UnitFormState;
}

function createEmptyState(): UnitFormState {
  return {
    unitCode: createInternalUnitCode(),
    unitLabel: '',
    floorLevel: '',
    areaSf: '',
    minDivisibleSf: '',
    availabilityStatus: 'available',
    possessionTimeline: 'immediate',
    netRentPerSf: '',
    additionalRentPerSf: '',
    incentives: '',
    potentiallyDivisible: false,
    contactEmail: '',
    displayOnPublicWebsite: true,
    heroImageUrl: '',
    galleryImageUrls: [],
    published: true,
  } satisfies UnitFormState;
}

function buildPayload({
  propertyId,
  state,
  unit,
}: {
  propertyId: string;
  state: UnitFormState;
  unit?: UnitSummary;
}): UnitFormValues {
  return {
    id: unit?.id,
    propertyId,
    unitCode: state.unitCode.trim(),
    unitLabel: state.unitLabel.trim(),
    floorLevel: normalizeFloorLevel(state.floorLevel),
    areaSf: Number(state.areaSf),
    minDivisibleSf: state.minDivisibleSf.trim().length > 0 ? Number(state.minDivisibleSf) : null,
    availabilityStatus: state.availabilityStatus,
    possessionTimeline: state.possessionTimeline,
    rentBasis: unit?.rent.rentBasis ?? 'net',
    netRentPerSf: roundCurrency(Number(state.netRentPerSf)),
    additionalRentPerSf:
      state.additionalRentPerSf.trim().length > 0
        ? roundCurrency(Number(state.additionalRentPerSf))
        : null,
    rentFrequency: 'annually',
    incentives: state.incentives.trim().length > 0 ? state.incentives.trim() : null,
    potentiallyDivisible: state.potentiallyDivisible,
    contactEmail: state.contactEmail.trim(),
    heroImageUrl: state.heroImageUrl.trim().length > 0 ? state.heroImageUrl.trim() : null,
    galleryImageUrls: state.galleryImageUrls,
    planGeometry: unit?.planGeometry ?? null,
    displayOnPublicWebsite: state.displayOnPublicWebsite,
    published: state.published,
  } satisfies UnitFormValues;
}

function toNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function toInput(value: number): string {
  return Number.isFinite(value) ? String(value) : '';
}

function normalizeFloorLevel(value: FloorLevelCode | ''): FloorLevelCode | null {
  return value === '' ? null : value;
}

function mapIssues(issues: ZodIssue[]): FormErrors {
  const next: FormErrors = {};
  for (const issue of issues) {
    const field = issue.path.at(0);
    if (typeof field === 'string' && next[field as keyof UnitFormState] === undefined) {
      next[field as keyof UnitFormState] = issue.message;
    }
  }
  return next;
}

function createCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `unit-form-${Date.now().toString()}`;
}
